import { QueryClient, MutationCache } from '@tanstack/react-query';

// Global mutation-cache invalidator: every successful mutation marks all
// queries stale, active queries refetch on the spot, idle queries refetch
// the next time their observer mounts. Pattern from TkDodo (TanStack Query
// maintainer): https://tkdodo.eu/blog/automatic-query-invalidation-after-mutations
//
// A mutation can opt out by setting `meta.invalidates: false`, or scope its
// invalidation by passing an explicit key array via `meta.invalidates`.
// Mutation-cache invalidator scoped to explicit `meta.invalidates` only.
//
// The original pattern (TkDodo's "invalidate everything on every mutation")
// is implemented at the axios layer (services/api.js) with path-scoped keys,
// so the broad catch-all here is redundant — and harmful: it doubled every
// mutation's invalidation, and a useMutation that wraps a single axios POST
// would invalidate twice (once narrow, once wide), amplifying the cancel-and-
// restart loop that pinned pages to their loading skeletons under rapid
// navigation.
//
// Mutations that need explicit invalidation now opt in via
// `meta.invalidates: [['key'], ...]`. Setting `meta.invalidates: false` is a
// no-op (kept for clarity at call sites).
const mutationCache = new MutationCache({
  onSuccess: (_data, _vars, _ctx, mutation) => {
    const meta = mutation.meta;
    if (!Array.isArray(meta?.invalidates)) return;
    meta.invalidates.forEach((key) =>
      queryClient.invalidateQueries({ queryKey: key, cancelRefetch: false })
    );
  },
});

export const queryClient = new QueryClient({
  mutationCache,
  defaultOptions: {
    queries: {
      // 5-minute staleTime gives instant-feeling navigation: visiting a page
      // a second time within the window renders from cache without firing a
      // refetch. Mutations still invalidate via the mutationCache above, so
      // the user never sees stale data after their own writes;
      // refetchOnWindowFocus catches changes made in another tab when the
      // user returns. Pages that genuinely need fresher data can override
      // staleTime per-query.
      staleTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
});
